/**
 * The company's brand theme in the admin app (spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md,
 * sections 3.4 and 3.6): what the server's answer means for this page, and the tokens as CSS variables.
 *
 * GET /v1/branding/theme/effective says what a workspace paints with. Its tokens are worked out on the server (the
 * engine that holds every contrast rule is apps/api/src/modules/tenant-branding/theme/brand-theme.ts) and travel as
 * `--brand-light-*` and `--brand-dark-*` variables on <html>; components/app-theme/app-theme.css reads them under
 * `data-app-theme="brand"`. They are <html>'s for the same reason the attribute is: dialogs and menus render at the end
 * of <body>, and the first-paint script runs before <body> exists.
 *
 * `data-app-theme="brand"` NEVER WITHOUT TOKENS. The sheet maps --primary and the rest to those variables, and var() of
 * an unset variable breaks every colour on the page. So applyAppTheme (app-theme-dom.ts) is the one writer, and it asks
 * brandTokensOn first; the first-paint script checks the same list before it writes anything.
 *
 * THE CACHE (`hf-brand-tokens`, `{ owner, workspace, version, vars }`) is what lets the first paint have the variables before any
 * request answers. It belongs to one person: the script applies it only for the cookie's owner, so one person's company
 * colours never paint for the next on a shared browser, and a sign-in drops another person's
 * (reconcileBrandTokenCache). It belongs to one workspace as well, the one its read went out under: an MSSP user moves between
 * clients with no reload, so a cache that was only theirs would paint the last client's colours over the next client's page. What
 * it holds is read as hostile text, the way the cookie is: only `--brand-*` names, only six hex digits, and short of one variable
 * it is no cache at all.
 *
 * effectiveChoice is nullable on purpose. Someone who never chose carries no attribute and keeps the builder's look, and
 * Classic switches the builder off, so `saved ?? 'classic'` would change what they see.
 */
import { readPersistedWorkspaceId } from '@/lib/tenant/workspace-id-storage'
import type { IconSet } from '../icon-sets'
import { APP_BRAND_THEME, type AppThemeChoice } from './app-themes'

/** Every value is an upper-case `#RRGGBB`. The API's own type (the engine above) is the source. */
export interface BrandTokens {
  primary: string
  primaryForeground: string
  /**
   * `primary` under a pointer, with `primaryForeground` still on it. Optional because an API older than this token sends none: a
   * rollback, or a frontend deployed alone. Each reader has an answer for that, and tsc holds it to one. The admin app paints Classic,
   * the portal the utility's own hover, and the token list leaves the row out.
   */
  primaryHover?: string
  /** Fill of what must be seen against what is around it: switches, checkboxes, radios, progress, the active nav bar. */
  control: string
  controlForeground: string
  /** Focus ring on the page and the card. */
  ring: string
  /** Focus ring on brand-coloured surfaces: a filled primary button. */
  ringOnPrimary: string
  link: string
  sidebarPrimary: string
  sidebarPrimaryForeground: string
  chart: [string, string, string, string, string]
  /** The brand colour as text on the page (`text-primary`). */
  paletteText: string
}

export interface BrandTokenPair {
  light: BrandTokens
  dark: BrandTokens
}

/** GET /v1/branding/theme/effective. `tokens` can be null while `applied` is true (logo only, or no usable colour). */
export interface EffectiveBrandTheme {
  version: string
  admin: {
    applied: boolean
    strength: number
    tokens: BrandTokenPair | null
  }
  iconSet: { value: IconSet } | null
  /** How the sidebar marks the page you are on. Null unless the theme is applied; the sheet then draws its default. */
  sidebarMark: BrandSidebarMark | null
  companyName: string
}

/**
 * How the sidebar marks the page you are on, as the company chose it on the Brand theme page: a soft tint with a bar at the
 * sidebar's edge (the default), the bar alone, the tint alone, or the row filled with the brand colour. The API keeps its own
 * copy (apps/api/src/modules/tenant-branding/dto/brand-theme.dto.ts) and the theme page's vocabulary guard compares the two.
 */
export const BRAND_SIDEBAR_MARKS = ['tint-bar', 'bar', 'tint', 'filled'] as const
export type BrandSidebarMark = (typeof BRAND_SIDEBAR_MARKS)[number]
export const DEFAULT_BRAND_SIDEBAR_MARK: BrandSidebarMark = 'tint-bar'

/**
 * The switch components/app-theme/app-theme.css is keyed on, beside `data-app-theme="brand"`. The default is the sheet's own rule
 * and writes nothing, so only these ever reach <html>.
 */
export const BRAND_SIDEBAR_MARK_ATTRIBUTE = 'data-brand-sidebar-mark'
export const PAINTED_BRAND_SIDEBAR_MARKS: readonly BrandSidebarMark[] = BRAND_SIDEBAR_MARKS.filter((mark) => mark !== DEFAULT_BRAND_SIDEBAR_MARK)

export const isBrandSidebarMark = (value: unknown): value is BrandSidebarMark => (BRAND_SIDEBAR_MARKS as readonly unknown[]).includes(value)

/** Put the mark on <html>: one the sheet draws, else none (the default, null, or anything it does not know). */
export function applyBrandSidebarMark(mark: BrandSidebarMark | null, doc: Document = document): void {
  const root = doc.documentElement
  if ((PAINTED_BRAND_SIDEBAR_MARKS as readonly unknown[]).includes(mark)) root.setAttribute(BRAND_SIDEBAR_MARK_ATTRIBUTE, mark as BrandSidebarMark)
  else root.removeAttribute(BRAND_SIDEBAR_MARK_ATTRIBUTE)
}

export const BRAND_TOKENS_STORAGE_KEY = 'hf-brand-tokens'

/** The server's answer, one entry per workspace under this root. */
export const EFFECTIVE_BRAND_THEME_QUERY_KEY = ['branding', 'theme', 'effective'] as const

const MODES = ['light', 'dark'] as const
const FIELDS = [
  'primary',
  'primaryForeground',
  'primaryHover',
  'control',
  'controlForeground',
  'ring',
  'ringOnPrimary',
  'link',
  'sidebarPrimary',
  'sidebarPrimaryForeground',
  'paletteText',
] as const
const CHART_SERIES = [1, 2, 3, 4, 5] as const

const kebab = (field: string) => field.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)

/** Every variable the sheet reads: a brand is painted with all of these, or not at all. */
export const BRAND_VARIABLES: readonly string[] = MODES.flatMap((mode) => [
  ...FIELDS.map((field) => `--brand-${mode}-${kebab(field)}`),
  ...CHART_SERIES.map((series) => `--brand-${mode}-chart-${series}`),
])

/** The first-paint script holds to these two, written out in its own text. */
export const BRAND_VARIABLE_NAME = /^--brand-(light|dark)-[a-z0-9-]+$/
export const BRAND_VARIABLE_VALUE = /^#[0-9A-Fa-f]{6}$/

/** The tokens as the variables on <html>. */
export function brandTokenVars(tokens: BrandTokenPair): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const mode of MODES) {
    for (const field of FIELDS) {
      const value = tokens[mode][field]
      if (value !== undefined) vars[`--brand-${mode}-${kebab(field)}`] = value
    }
    for (const series of CHART_SERIES) vars[`--brand-${mode}-chart-${series}`] = tokens[mode].chart[series - 1]
  }
  return vars
}

/** What may reach <html> out of `vars`: the rest ignored, and null when a variable the sheet reads is missing. */
export function cleanBrandVars(vars: unknown): Record<string, string> | null {
  if (typeof vars !== 'object' || vars === null) return null
  const clean: Record<string, string> = {}
  for (const [name, value] of Object.entries(vars)) {
    if (BRAND_VARIABLE_NAME.test(name) && typeof value === 'string' && BRAND_VARIABLE_VALUE.test(value)) clean[name] = value
  }
  return BRAND_VARIABLES.every((name) => clean[name] !== undefined) ? clean : null
}

/** Put the variables on <html>. Returns whether it did: a set it cannot use writes nothing. */
export function applyBrandTokens(vars: unknown, doc: Document = document): boolean {
  const clean = cleanBrandVars(vars)
  if (clean === null) return false
  const { style } = doc.documentElement
  for (const [name, value] of Object.entries(clean)) style.setProperty(name, value)
  return true
}

/** Is every variable the sheet reads on <html>? The question before `data-app-theme="brand"` is written. */
export function brandTokensOn(doc: Document = document): boolean {
  const { style } = doc.documentElement
  return BRAND_VARIABLES.every((name) => BRAND_VARIABLE_VALUE.test(style.getPropertyValue(name).trim()))
}

/** Take the variables and the sidebar mark off <html>, leaving the rest of its style (next-themes' color-scheme) as it is. */
export function clearBrandTokens(doc: Document = document): void {
  const root = doc.documentElement
  root.removeAttribute(BRAND_SIDEBAR_MARK_ATTRIBUTE)
  const { style } = root
  for (let i = style.length - 1; i >= 0; i--) {
    const name = style.item(i)
    if (name.startsWith('--brand-')) style.removeProperty(name)
  }
  if (root.getAttribute('style') === '') root.removeAttribute('style')
}

export interface BrandTokenCache {
  owner: string
  /** The workspace it was read for. Null: there was none to name, and it then answers for no workspace. */
  workspace: string | null
  version: string
  vars: Record<string, string>
  /** The sidebar mark painted with these colours. One it does not know, or none (a cache from before the mark), reads as the default. */
  mark: BrandSidebarMark
}

/** The cache, for its owner whatever workspace it names: anything else, unreadable text or storage that throws, is no cache. */
function readOwnersBrandTokenCache(owner: string | null | undefined): BrandTokenCache | null {
  if (!owner) return null
  try {
    const entry: unknown = JSON.parse(window.localStorage.getItem(BRAND_TOKENS_STORAGE_KEY) ?? 'null')
    if (typeof entry !== 'object' || entry === null) return null
    const { owner: holder, workspace, version, vars, mark } = entry as Record<string, unknown>
    if (holder !== owner || typeof version !== 'string') return null
    const clean = cleanBrandVars(vars)
    return (
      clean && {
        owner,
        workspace: typeof workspace === 'string' ? workspace : null,
        version,
        vars: clean,
        mark: isBrandSidebarMark(mark) ? mark : DEFAULT_BRAND_SIDEBAR_MARK,
      }
    )
  } catch {
    return null
  }
}

/** The cache, for its owner and the workspace it answers for: by default the one the API client names, which is what the script reads. */
export function readBrandTokenCache(owner: string | null | undefined, workspaceId: string | null = readPersistedWorkspaceId()): BrandTokenCache | null {
  const cache = readOwnersBrandTokenCache(owner)
  return cache?.workspace === workspaceId ? cache : null
}

export function writeBrandTokenCache(
  owner: string,
  version: string,
  vars: Record<string, string>,
  workspaceId: string | null = readPersistedWorkspaceId(),
  mark: BrandSidebarMark = DEFAULT_BRAND_SIDEBAR_MARK,
): void {
  try {
    window.localStorage.setItem(BRAND_TOKENS_STORAGE_KEY, JSON.stringify({ owner, workspace: workspaceId, version, vars, mark }))
  } catch {
    // Private mode or quota: the next first paint has no variables, and so paints no brand. Nothing else follows.
  }
}

export function clearBrandTokenCache(): void {
  try {
    window.localStorage.removeItem(BRAND_TOKENS_STORAGE_KEY)
  } catch {
    // Storage that throws holds nothing to clear.
  }
}

/** A sign-in: keep this person's cache, whatever workspace it names, drop anyone else's and one that cannot be read. */
export function reconcileBrandTokenCache(userId: string | null | undefined): void {
  try {
    if (window.localStorage.getItem(BRAND_TOKENS_STORAGE_KEY) === null) return
  } catch {
    return
  }
  if (readOwnersBrandTokenCache(userId) === null) clearBrandTokenCache()
}

/** The tokens the admin app can paint with: none unless the theme is applied and the server worked some out. */
export const usableBrandTokens = (effective: EffectiveBrandTheme): BrandTokenPair | null =>
  effective.admin.applied ? effective.admin.tokens : null

/**
 * What the page paints, from the person's own choice and the server's answer. Null is no attribute at all. A company's brand
 * is its default: it paints for someone who never chose, and it never changes a choice.
 */
export function effectiveChoice(saved: AppThemeChoice | null, effective: EffectiveBrandTheme): AppThemeChoice | null {
  if (usableBrandTokens(effective) === null) return saved === APP_BRAND_THEME ? 'classic' : saved
  return saved ?? APP_BRAND_THEME
}

function paintableTokens(value: unknown): BrandTokenPair | null {
  try {
    return cleanBrandVars(brandTokenVars(value as BrandTokenPair)) ? (value as BrandTokenPair) : null
  } catch {
    return null
  }
}

/**
 * The server's answer as this page reads it, or null when it is not one: a failed read, which leaves the last painted
 * look alone. Tokens it could not paint with are no tokens, so the page paints Classic and not unset variables. The API
 * sends `locked` as well, for tabs still open on an older bundle; nothing here reads it.
 */
export function parseEffectiveBrandTheme(answer: unknown): EffectiveBrandTheme | null {
  if (typeof answer !== 'object' || answer === null) return null
  const { version, admin, iconSet, sidebarMark, companyName } = answer as Record<string, unknown>
  if (typeof version !== 'string' || typeof admin !== 'object' || admin === null) return null
  const { applied, strength, tokens } = admin as Record<string, unknown>
  if (typeof applied !== 'boolean') return null
  const set = iconSet as EffectiveBrandTheme['iconSet']
  return {
    version,
    admin: { applied, strength: typeof strength === 'number' ? strength : 2, tokens: tokens === null ? null : paintableTokens(tokens) },
    iconSet: set && typeof set.value === 'string' ? { value: set.value } : null,
    sidebarMark: isBrandSidebarMark(sidebarMark) ? sidebarMark : null,
    companyName: typeof companyName === 'string' ? companyName : '',
  }
}
